/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import type { PublicationRow } from './types';

/** The two families a property's channels are presented in. */
export type ChannelGroupKey = 'social' | 'portals';

const SOCIAL_PORTALS = new Set(['facebook', 'instagram']);

/**
 * Which family a portal belongs to: social networks, or listing portals
 * (OLX, ZAP, Viva Real, Chaves na Mão and any future one).
 */
export function channelGroupOf(portal: string): ChannelGroupKey {
  return SOCIAL_PORTALS.has(portal) ? 'social' : 'portals';
}

/**
 * Brand colour of a channel, as a Tailwind background class, so each channel
 * is recognisable at a glance. Unknown portals get a neutral tone.
 */
const CHANNEL_DOT_CLASS: Record<string, string> = {
  facebook: 'bg-[#1877F2]',
  instagram: 'bg-[#E4405F]',
  olx: 'bg-[#5A3E7A]',
  zap: 'bg-[#2D5A6B]',
  vivareal: 'bg-[#3D5A47]',
  chavesnamao: 'bg-[#8B6914]',
};

/** The background class of a channel's colour dot, neutral for unknown channels. */
export function channelDotClass(portal: string): string {
  return CHANNEL_DOT_CLASS[portal] ?? 'bg-(--text-tertiary)';
}

export interface ChannelGroup {
  key: ChannelGroupKey;
  rows: PublicationRow[];
}

const GROUP_ORDER: ChannelGroupKey[] = ['social', 'portals'];

/**
 * Splits publication rows into their channel families, in a fixed order and
 * keeping each row's incoming order. A family with no rows is left out.
 *
 * @param rows - The merged publication rows of a property.
 * @returns The non-empty groups, social networks first.
 */
export function groupRowsByChannel(rows: PublicationRow[]): ChannelGroup[] {
  return GROUP_ORDER
    .map((key) => ({ key, rows: rows.filter((row) => channelGroupOf(row.portal) === key) }))
    .filter((group) => group.rows.length > 0);
}

/**
 * Connected channels that have nothing published for this property yet, so
 * the agency can see where it is not and publish there.
 *
 * @param rows - The merged publication rows of a property.
 * @param connectedPortals - Portal ids connected for the agency.
 * @returns The connected portal ids with no row, in connection order, without duplicates.
 */
export function unpublishedPortals(rows: PublicationRow[], connectedPortals: string[]): string[] {
  const withRows = new Set(rows.map((row) => row.portal));
  return [...new Set(connectedPortals)].filter((portal) => !withRows.has(portal));
}

export interface ChannelSummary {
  /** Channels with at least one published post/listing. */
  liveChannels: number;
  /** Every channel that is connected or has any publication. */
  totalChannels: number;
  /** Total publications (rows), whatever their state. */
  publications: number;
  /** Publications carrying a running Lead Ad or boost. */
  withActiveAds: number;
  /** Publications that failed, or whose Lead Ad could not be created. */
  needingAttention: number;
}

function isFailed(row: PublicationRow): boolean {
  return row.status === 'error' || row.status === 'publish_failed' || row.lead_ad_status === 'failed';
}

/** Whether the row has an unpaused lead ad or boost running. */
function hasRunningAd(row: PublicationRow): boolean {
  const leadAdRunning = (row.lead_ad_status === 'created' || Boolean(row.ad_id)) && !row.lead_ad_paused;
  const boostRunning = (row.boosts ?? []).some((boost) => !boost.paused_at);
  return leadAdRunning || boostRunning;
}

/**
 * The numbers shown in the summary strip, all derived from what the
 * publication rows and the connected channels already say.
 *
 * @param rows - The merged publication rows of a property.
 * @param connectedPortals - Portal ids connected for the agency.
 * @returns Counts for the summary tiles.
 */
export function summarizeChannels(rows: PublicationRow[], connectedPortals: string[]): ChannelSummary {
  const live = new Set(rows.filter((row) => row.status === 'published').map((row) => row.portal));
  const all = new Set([...connectedPortals, ...rows.map((row) => row.portal)]);
  return {
    liveChannels: live.size,
    totalChannels: all.size,
    publications: rows.length,
    withActiveAds: rows.filter(hasRunningAd).length,
    needingAttention: rows.filter(isFailed).length,
  };
}
